/**
 * Core Go Game Types
 *
 * All TypeScript types and interfaces for the Go game.
 * Supports 9×9, 13×13, and 19×19 boards.
 * Two players: Black and White. Black goes first.
 * Chinese rules (area scoring) with 6.5 komi.
 */

// ============================================================================
// Board Configuration
// ============================================================================

/** Stone color on the board */
export type GoColor = 'black' | 'white';

/** Player identifier */
export type GoPlayer = 'player1' | 'player2';

/** AI difficulty levels */
export type GoDifficulty = 'easy' | 'medium' | 'hard';

/** Board cell: stone color or null (empty) */
export type GoCell = GoColor | null;

/** 2D board array */
export type GoBoard = GoCell[][];

/** Supported board sizes */
export const BOARD_SIZES = [9, 13, 19] as const;
export type GoBoardSize = typeof BOARD_SIZES[number];

/** Default board size */
export const DEFAULT_BOARD_SIZE: GoBoardSize = 19;

/** Default komi (compensation for White) */
export const DEFAULT_KOMI = 6.5;

/** Directions for adjacency: up, down, left, right */
export const DIRECTIONS: [number, number][] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

// ============================================================================
// Moves
// ============================================================================

/** A stone placement or a pass */
export interface GoStoneMove {
  type: 'place';
  row: number;
  col: number;
}

export interface GoPassMove {
  type: 'pass';
}

export type GoMove = GoStoneMove | GoPassMove;

/** A recorded move in history */
export interface GoMoveRecord {
  player: GoColor;
  move: GoMove;
  timestamp: number;
  captures: GoPosition[];
}

/** Board position */
export interface GoPosition {
  row: number;
  col: number;
}

// ============================================================================
// Scoring
// ============================================================================

export interface GoScore {
  black: number;
  white: number;
  blackTerritory: number;
  whiteTerritory: number;
  blackStones: number;
  whiteStones: number;
  komi: number;
}

/** Territory markers for end-of-game display */
export type GoTerritoryMap = ('black' | 'white' | null)[][];

// ============================================================================
// Game State
// ============================================================================

export interface GoGameState {
  board: GoBoard;
  boardSize: GoBoardSize;
  currentPlayer: GoColor;
  captures: { black: number; white: number };
  moveHistory: GoMoveRecord[];
  koPoint: GoPosition | null;
  consecutivePasses: number;
  gameStatus: 'active' | 'completed';
  winner?: GoColor | 'draw';
  score?: GoScore;
  territory?: GoTerritoryMap;
  komi: number;
  gameId: string;
  startedAt: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface GoGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: GoDifficulty;
  playerColor?: GoColor;
  boardSize: GoBoardSize;
}

// ============================================================================
// Statistics
// ============================================================================

export interface GoGameStats {
  wins: number;
  losses: number;
  draws: number;
  winPercentage: number;
  averageScore: number;
  highestScore: number;
  totalCaptures: number;
}

export interface GoStatistics {
  userId?: string;
  singlePlayer: {
    easy: GoGameStats;
    medium: GoGameStats;
    hard: GoGameStats;
  };
  localMultiplayer: GoGameStats;
  onlineMultiplayer: GoGameStats;
  totalGames: number;
  totalPlayTime: number;
}

export interface GoGameResult {
  gameId: string;
  mode: GoGameSettings['mode'];
  difficulty?: GoDifficulty;
  winner?: GoColor | 'draw';
  score: GoScore;
  totalMoves: number;
  totalCaptures: number;
  duration: number;
  completedAt: number;
  boardSize: GoBoardSize;
}

// ============================================================================
// Component Props
// ============================================================================

export interface GoGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface GoGameModeSelectorProps {
  onStartGame: (settings: GoGameSettings) => void;
  onJoinGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

export interface GoBoardProps {
  board: GoBoard;
  boardSize: GoBoardSize;
  validMoves: GoPosition[];
  lastMove: GoPosition | null;
  currentPlayer: GoColor;
  koPoint: GoPosition | null;
  territory?: GoTerritoryMap;
  gameOver: boolean;
  onIntersectionClick: (position: GoPosition) => void;
  disabled?: boolean;
}

// ============================================================================
// Star Points (Hoshi)
// ============================================================================

/** Standard star point positions for each board size */
export const STAR_POINTS: Record<GoBoardSize, GoPosition[]> = {
  9: [
    { row: 2, col: 2 }, { row: 2, col: 6 },
    { row: 4, col: 4 },
    { row: 6, col: 2 }, { row: 6, col: 6 },
  ],
  13: [
    { row: 3, col: 3 }, { row: 3, col: 6 }, { row: 3, col: 9 },
    { row: 6, col: 3 }, { row: 6, col: 6 }, { row: 6, col: 9 },
    { row: 9, col: 3 }, { row: 9, col: 6 }, { row: 9, col: 9 },
  ],
  19: [
    { row: 3, col: 3 }, { row: 3, col: 9 }, { row: 3, col: 15 },
    { row: 9, col: 3 }, { row: 9, col: 9 }, { row: 9, col: 15 },
    { row: 15, col: 3 }, { row: 15, col: 9 }, { row: 15, col: 15 },
  ],
};
